import { closeSync, openSync, readSync } from "node:fs";
import { resolve } from "node:path";

import { fetchWithRetry } from "./network.js";
import {
  containsCredentialPattern,
  WORKLOAD_DIAGNOSIS_SCHEMA_VERSION,
  WorkloadDiagnosisRequestSchema,
  WorkloadDiagnosisResponseSchema,
  type WorkloadDiagnosisRequest,
  type WorkloadDiagnosisResponse,
} from "./workload-diagnosis-schema.js";

export {
  WORKLOAD_DIAGNOSIS_SCHEMA_VERSION,
  type WorkloadDiagnosisRequest,
  type WorkloadDiagnosisResponse,
} from "./workload-diagnosis-schema.js";

export const WORKLOAD_DIAGNOSIS_PATH = "/v1/doctor/workload";
export const WORKLOAD_DIAGNOSIS_TIMEOUT_MS = 12_000;
export const MAX_WORKLOAD_DIAGNOSIS_REQUEST_BYTES = 16_384;
export const MAX_WORKLOAD_DIAGNOSIS_RESPONSE_BYTES = 65_536;
const PUBLIC_WORKLOAD_DIAGNOSIS_BASE_URL = "https://useorgx.com";
const MAX_HANDOFF_TOKEN_CHARACTERS = 7_000;

export interface RequestWorkloadDiagnosisOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

const SENSITIVE_KEY =
  /(?:^|[_-])(?:authorization|cookie|credentials?|password|secrets?|session|tokens?|api[_-]?keys?|access[_-]?tokens?|refresh[_-]?tokens?|client[_-]?secrets?|private[_-]?keys?|database[_-]?(?:url|uri)|db[_-]?(?:url|uri))(?:$|[_-])/i;

function readBoundedUtf8(source: string): string {
  const shouldClose = source !== "-";
  const fd = shouldClose ? openSync(resolve(source), "r") : 0;
  const buffer = Buffer.alloc(MAX_WORKLOAD_DIAGNOSIS_REQUEST_BYTES + 1);
  let offset = 0;

  try {
    while (offset < buffer.byteLength) {
      const bytesRead = readSync(
        fd,
        buffer,
        offset,
        buffer.byteLength - offset,
        null
      );
      if (bytesRead === 0) break;
      offset += bytesRead;
    }
  } finally {
    if (shouldClose) closeSync(fd);
  }

  if (offset > MAX_WORKLOAD_DIAGNOSIS_REQUEST_BYTES) {
    throw new Error(
      `Workload diagnosis stopped before parsing: input exceeds ${MAX_WORKLOAD_DIAGNOSIS_REQUEST_BYTES} bytes.`
    );
  }
  return buffer.subarray(0, offset).toString("utf8");
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function findSensitivePath(
  value: unknown,
  path = "",
  depth = 0
): string | null {
  if (depth > 20) return path || "/";
  if (typeof value === "string" && containsCredentialPattern(value))
    return path || "/";
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const found = findSensitivePath(
        value[index],
        `${path}/${index}`,
        depth + 1
      );
      if (found) return found;
    }
    return null;
  }
  const record = asRecord(value);
  if (!record) return null;
  for (const [key, child] of Object.entries(record)) {
    const escapedKey = key.replaceAll("~", "~0").replaceAll("/", "~1");
    const childPath = `${path}/${escapedKey}`;
    if (SENSITIVE_KEY.test(key)) return childPath;
    const found = findSensitivePath(child, childPath, depth + 1);
    if (found) return found;
  }
  return null;
}

function parseCredentialFreeWorkload(value: unknown): WorkloadDiagnosisRequest {
  const sensitivePath = findSensitivePath(value);
  if (sensitivePath) {
    throw new Error(
      `Workload diagnosis stopped before upload: remove credentials or secret-shaped values at ${sensitivePath}.`
    );
  }

  const parsed = WorkloadDiagnosisRequestSchema.safeParse(value);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const path = issue?.path.length ? ` at /${issue.path.join("/")}` : "";
    throw new Error(
      `Workload input does not match schema ${WORKLOAD_DIAGNOSIS_SCHEMA_VERSION}${path}.`
    );
  }
  return parsed.data;
}

export function assertCredentialFreeWorkload(
  value: unknown
): asserts value is WorkloadDiagnosisRequest {
  parseCredentialFreeWorkload(value);
}

export function readWorkloadDiagnosisInput(
  source: string
): WorkloadDiagnosisRequest {
  const raw = readBoundedUtf8(source);
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    throw new Error(
      `Workload input ${
        source === "-" ? "from stdin" : source
      } must be valid JSON.`
    );
  }
  return parseCredentialFreeWorkload(value);
}

function isLoopbackHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase().replace(/^\[|\]$/g, "");
  return ["localhost", "127.0.0.1", "::1"].includes(normalized);
}

export function resolveWorkloadDiagnosisUrl(baseUrl?: string): string {
  const candidate = baseUrl?.trim() || PUBLIC_WORKLOAD_DIAGNOSIS_BASE_URL;
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new Error("Workload diagnosis base URL must be a valid absolute URL.");
  }
  if (
    parsed.username ||
    parsed.password ||
    (parsed.protocol !== "https:" &&
      !(parsed.protocol === "http:" && isLoopbackHostname(parsed.hostname)))
  ) {
    throw new Error(
      "Workload diagnosis base URL must use HTTPS, except for an explicit loopback test URL."
    );
  }

  parsed.search = "";
  parsed.hash = "";
  parsed.pathname = parsed.pathname.replace(/\/api\/?$/, "").replace(/\/+$/, "");
  const normalizedBase = parsed.toString().replace(/\/+$/, "");
  return `${normalizedBase}/api${WORKLOAD_DIAGNOSIS_PATH}`;
}

function parseErrorMessage(status: number, payload: unknown): string {
  const record = asRecord(payload);
  const error = asRecord(record?.error);
  const message =
    typeof error?.message === "string" &&
    error.message.length <= 300 &&
    !/[\u0000-\u001f\u007f-\u009f]/.test(error.message)
      ? error.message
      : null;
  return message ?? `Workload diagnosis failed with HTTP ${status}.`;
}

async function readBoundedJsonResponse(response: Response): Promise<unknown> {
  const declaredLength = Number(response.headers.get("content-length"));
  if (
    Number.isFinite(declaredLength) &&
    declaredLength > MAX_WORKLOAD_DIAGNOSIS_RESPONSE_BYTES
  ) {
    throw new Error("Workload diagnosis response exceeded the safe size limit.");
  }
  if (!response.body) return null;

  const chunks: Uint8Array[] = [];
  const reader = response.body.getReader();
  let total = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > MAX_WORKLOAD_DIAGNOSIS_RESPONSE_BYTES) {
      await reader.cancel().catch(() => undefined);
      throw new Error("Workload diagnosis response exceeded the safe size limit.");
    }
    chunks.push(value);
  }

  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return text ? JSON.parse(text) : null;
  } catch {
    throw new Error("Workload diagnosis returned invalid UTF-8 JSON.");
  }
}

function assertSafeHandoffUrl(
  diagnosis: WorkloadDiagnosisResponse,
  endpointUrl: string
): void {
  const value = diagnosis.approval_handoff.url;
  if (!value) return;

  let handoff: URL;
  try {
    handoff = new URL(value);
  } catch {
    throw new Error("Workload diagnosis returned an invalid approval handoff URL.");
  }
  const allowedOrigins = new Set([
    new URL(endpointUrl).origin,
    new URL(PUBLIC_WORKLOAD_DIAGNOSIS_BASE_URL).origin,
  ]);
  const fragment = new URLSearchParams(handoff.hash.slice(1));
  const plan = fragment.get("plan");
  if (
    !allowedOrigins.has(handoff.origin) ||
    handoff.protocol !== "https:" &&
      !(handoff.protocol === "http:" && isLoopbackHostname(handoff.hostname)) ||
    handoff.username ||
    handoff.password ||
    handoff.pathname !== "/install/workload-plan" ||
    handoff.search ||
    [...fragment.keys()].some((key) => key !== "plan") ||
    fragment.getAll("plan").length !== 1 ||
    !plan ||
    plan.length > MAX_HANDOFF_TOKEN_CHARACTERS
  ) {
    throw new Error("Workload diagnosis returned an unsafe approval handoff URL.");
  }
}

export async function requestWorkloadDiagnosis(
  input: WorkloadDiagnosisRequest,
  options: RequestWorkloadDiagnosisOptions = {}
): Promise<WorkloadDiagnosisResponse> {
  const safeInput = parseCredentialFreeWorkload(input);
  const body = JSON.stringify(safeInput);
  if (
    new TextEncoder().encode(body).byteLength >
    MAX_WORKLOAD_DIAGNOSIS_REQUEST_BYTES
  ) {
    throw new Error(
      `Workload diagnosis stopped before upload: input exceeds ${MAX_WORKLOAD_DIAGNOSIS_REQUEST_BYTES} bytes.`
    );
  }
  const init = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    redirect: "error",
  } satisfies RequestInit;
  const endpointUrl = resolveWorkloadDiagnosisUrl(options.baseUrl);
  const response = options.fetchImpl
    ? await options.fetchImpl(endpointUrl, {
        ...init,
        signal: AbortSignal.timeout(
          options.timeoutMs ?? WORKLOAD_DIAGNOSIS_TIMEOUT_MS
        ),
      })
    : await fetchWithRetry(endpointUrl, init, {
        timeoutMs: options.timeoutMs ?? WORKLOAD_DIAGNOSIS_TIMEOUT_MS,
        retries: 0,
      });
  const payload = await readBoundedJsonResponse(response);
  if (!response.ok)
    throw new Error(parseErrorMessage(response.status, payload));

  const parsed = WorkloadDiagnosisResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(
      "Workload diagnosis returned an incompatible response contract."
    );
  }
  assertSafeHandoffUrl(parsed.data, endpointUrl);
  return parsed.data;
}
